/**
 * Web Adapter - Browser environment platform implementation
 * Provides graceful degradation for desktop-only features
 */

import type { PlatformAPI, UploadResult } from './types';

// In-memory logger sink for web
const webLogs: Array<{ time: string; msg: string }> = [];
const eventListeners: Map<string, Set<(...args: unknown[]) => void>> = new Map();

// Logger callbacks
const logCallbacks: Set<(msg: string) => void> = new Set();
const notifCallbacks: Set<(payload: { type: string; message: string }) => void> = new Set();

// In-memory config storage using localStorage when available
const LLM_CONFIG_KEY = 'exif-studio:llm-config';

// Base URL for web backend
export const getBackendUrl = (): string => {
  if (typeof window !== 'undefined') {
    // If served via a different port (e.g. Python server), fallback to 8081 for API
    if (window.location.port !== '8081' && window.location.port !== '') {
      return `${window.location.protocol}//${window.location.hostname}:8081`;
    }
    return window.location.origin;
  }
  return 'http://localhost:8081';
};

class WebPlatformAdapter implements PlatformAPI {
  private instanceId: string;

  constructor() {
    this.instanceId = this.generateUUID();
  }

  private generateUUID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // Fallback: RFC4122 version 4 UUID
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  get isElectron(): boolean {
    return false;
  }

  setInstanceId(id: string): void {
    this.instanceId = id;
  }

  getInstanceId(): string | null {
    return this.instanceId;
  }

  // No-op invoke for web - returns promises that resolve with fallback values
  async invoke(channel: string, ...args: unknown[]): Promise<unknown> {
    switch (channel) {
      case 'llm:get-config':
        try {
          const stored = localStorage.getItem(LLM_CONFIG_KEY);
          return stored ? JSON.parse(stored) : null;
        } catch {
          return null;
        }

      case 'llm:save-config':
        try {
          if (args[0]) {
            localStorage.setItem(LLM_CONFIG_KEY, JSON.stringify(args[0]));
          }
          return true;
        } catch {
          return false;
        }

      case 'llm:generate-exif':
        if (args[0] && typeof args[1] === 'string' && typeof args[2] === 'string') {
          return this.generateLLMExif(args[0] as any, args[1], args[2]);
        }
        return { error: 'Invalid arguments for LLM generation' };

      case 'folder:select':
        return null;

      case 'images:get':
        // Fetch images from backend for this instance
        return this.fetchImages();

      case 'exif:read':
        // Fetch EXIF from backend for the given path
        if (typeof args[0] === 'string') {
          return this.fetchExif(args[0]);
        }
        return {};

      case 'exif:read-batch':
        if (Array.isArray(args[0])) {
          const results: Record<string, Record<string, unknown>> = {};
          for (const path of args[0]) {
            results[path] = await this.fetchExif(path);
          }
          return results;
        }
        return {};

      case 'exif:write':
        // In web mode, write is handled via applyChanges
        return { success: true };

      case 'app:get-path':
        // Not applicable in web mode
        return null;

      default:
        console.warn(`[Web Mode] IPC invoke not available: ${channel}`);
        return null;
    }
  }

  async uploadFiles(files: FileList): Promise<UploadResult | null> {
    if (!files.length) return null;

    const formData = new FormData();
    Array.from(files).forEach(f => formData.append('files', f));

    const backendUrl = getBackendUrl();
    const res = await fetch(`${backendUrl}/api/upload?instanceId=${this.instanceId}`, {
      method: 'POST',
      body: formData
    });

    if (!res.ok) {
      console.error('[Web Mode] Upload failed:', res.statusText);
      return null;
    }

    return res.json();
  }

  private async fetchImages(): Promise<Array<{ name: string; path: string; size: number; modified: string; thumbnail?: string }>> {
    const backendUrl = getBackendUrl();
    try {
      const res = await fetch(`${backendUrl}/api/images/${this.instanceId}`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.map((img: { name: string; path: string; size: number }) => ({
        ...img,
        thumbnail: `${backendUrl}/api/serve/${this.instanceId}/${img.name}`,
        modified: new Date().toISOString()
      }));
    } catch {
      return [];
    }
  }

  private async fetchExif(relativePath: string): Promise<Record<string, unknown>> {
    const backendUrl = getBackendUrl();
    try {
      const filename = relativePath.split('/').pop();
      if (!filename) return {};
      const res = await fetch(`${backendUrl}/api/exif/${this.instanceId}/${filename}`);
      if (!res.ok) return {};
      return res.json();
    } catch {
      return {};
    }
  }

  public async applyChanges(changes: Array<{ path: string; tags: Record<string, unknown> }>): Promise<Array<{ path: string; success: boolean; error?: string }>> {
    const backendUrl = getBackendUrl();
    try {
      const res = await fetch(`${backendUrl}/api/apply/${this.instanceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changes })
      });
      if (!res.ok) {
        // Try to parse error from response
        let errorMsg = 'Failed to apply changes';
        try {
          const errData = await res.json();
          errorMsg = errData.error || errorMsg;
        } catch {}
        return changes.map(c => ({ path: c.path, success: false, error: errorMsg }));
      }

      // The server returns a zip file - trigger browser download
      const contentType = res.headers.get('Content-Type') || '';
      if (contentType.includes('application/zip') || contentType.includes('application/octet-stream')) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'exif-studio-modified.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return changes.map(c => ({ path: c.path, success: true }));
      }

      // Fallback: if server returned JSON (shouldn't happen with new workflow)
      const jsonResults = await res.json();
      return jsonResults;
    } catch (e: any) {
      return changes.map(c => ({ path: c.path, success: false, error: e.message }));
    }
  }

  public async generateLLMExif(config: Record<string, unknown>, imagePath: string, promptTemplate?: string | undefined): Promise<unknown> {
    const backendUrl = getBackendUrl();
    try {
      const res = await fetch(`${backendUrl}/api/llm/generate/${this.instanceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config, imagePath, promptTemplate })
      });
      if (!res.ok) {
        const text = await res.text();
        return { error: `LLM generation failed: ${res.status} ${text}` };
      }
      return res.json();
    } catch (e: any) {
      return { error: e.message };
    }
  }

  // Event subscription using internal dispatcher
  on(channel: string, callback: (...args: unknown[]) => void): (() => void) | void {
    if (channel === 'log') {
      logCallbacks.add(callback as (msg: string) => void);
      return () => logCallbacks.delete(callback as (msg: string) => void);
    }
    if (channel === 'notification') {
      notifCallbacks.add(callback as (payload: { type: string; message: string }) => void);
      return () => notifCallbacks.delete(callback as (payload: { type: string; message: string }) => void);
    }

    if (!eventListeners.has(channel)) {
      eventListeners.set(channel, new Set());
    }
    eventListeners.get(channel)!.add(callback);

    return () => {
      eventListeners.get(channel)?.delete(callback);
    };
  }

  off(channel: string, callback: (...args: unknown[]) => void): void {
    if (channel === 'log') {
      logCallbacks.delete(callback as (msg: string) => void);
      return;
    }
    if (channel === 'notification') {
      notifCallbacks.delete(callback as (payload: { type: string; message: string }) => void);
      return;
    }
    eventListeners.get(channel)?.delete(callback);
  }

  // File System Access API for folder selection (when available)
  async selectFolder(): Promise<string | null> {
    // Try File System Access API
    if ('showDirectoryPicker' in window) {
      try {
        const handle = await (window as typeof window & { showDirectoryPicker?(): Promise<FileSystemDirectoryHandle> }).showDirectoryPicker!();
        // FileSystemHandle has a name property
        return (handle as unknown as { name: string }).name;
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          console.warn('[Web Mode] Directory picker error:', err);
        }
        return null;
      }
    }

    // Fall back to alert
    alert('Folder selection requires Electron desktop app or a modern browser with File System Access API support.');
    return null;
  }

  async getImages(): Promise<Array<{ name: string; path: string; size: number; modified: string }>> {
    return this.fetchImages();
  }

  async readExif(path: string): Promise<Record<string, unknown>> {
    return this.fetchExif(path);
  }

  async readBatchExif(paths: string[]): Promise<Record<string, Record<string, unknown>>> {
    const results: Record<string, Record<string, unknown>> = {};
    for (const p of paths) {
      results[p] = await this.fetchExif(p);
    }
    return results;
  }



  async saveLLMConfig(config: Record<string, unknown>): Promise<boolean> {
    try {
      localStorage.setItem(LLM_CONFIG_KEY, JSON.stringify(config));
      return true;
    } catch {
      return false;
    }
  }

  async getLLMConfig(): Promise<Record<string, unknown> | null> {
    try {
      const stored = localStorage.getItem(LLM_CONFIG_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  async getThumbnail(): Promise<string> {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23666"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23fff" font-family="sans-serif" font-size="12%22%3ENo Preview%3C/text%3E%3C/svg%3E';
  }

  showNotification(type: 'info' | 'success' | 'warning' | 'error', message: string): void {
    const payload = { type, message };
    notifCallbacks.forEach(cb => cb(payload));
  }

  logMessage(message: string): void {
    const time = new Date().toLocaleTimeString();
    webLogs.unshift({ time, msg: message });
    if (webLogs.length > 500) webLogs.length = 500;
    logCallbacks.forEach(cb => cb(message));
    console.log(`[Web Log] ${time}: ${message}`);
  }
}

// Create singleton instance
export const webAdapter = new WebPlatformAdapter();

// Helper to trigger events from UI components
export function triggerWebEvent(channel: string, ...args: unknown[]): void {
  eventListeners.get(channel)?.forEach(cb => cb(...args));
}

// Expose web adapter as window.electronAPI for compatibility
if (typeof window !== 'undefined' && !window.electronAPI) {
  // Use double cast to avoid TypeScript overlap checking issues
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).electronAPI = webAdapter;
}

export default webAdapter;
